import React, { useMemo, useState } from 'react';

import { FormControl, FormHelperText, InputLabel } from '@material-ui/core';
import type { FormikProps } from 'formik';
import { get } from 'lodash';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import './FormRichTextInput.styles.css';

type QuillModules = {
  toolbar?: unknown;
  keyboard?: unknown;
  history?: unknown;
  clipboard?: unknown;
  [key: string]: unknown;
};

type FormRichTextInputProps<T> = {
  formik: FormikProps<T>;
  id: string;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  readonly?: boolean;
  readonlyInfo?: string;
  className?: string;
  placeholder?: string;
  modules?: QuillModules;
  formats?: string[];
  height?: number;
};

const FormRichTextInput = <T extends Record<string, unknown>>({
  formik,
  id,
  label,
  disabled = false,
  required = false,
  readonly = false,
  readonlyInfo,
  className,
  placeholder,
  modules,
  formats,
  height = 200,
}: FormRichTextInputProps<T>) => {
  const { touched, errors, setFieldValue, setFieldTouched, values } = formik;

  const value = get(values, id, '') as string;
  const error = get(errors, id) as string | undefined;
  const isTouched = get(touched, id) as boolean | undefined;

  const defaultModules = useMemo(
    () => ({
      clipboard: {
        matchVisual: false,
      },
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        ['link'],
        ['clean'],
      ],
    }),
    [],
  );

  const defaultFormats = useMemo(() => ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'indent', 'link'], []);

  const unifiedLabel = useMemo(() => {
    if (!label) return '';
    return required ? `${label} *` : label;
  }, [label, required]);

  const handleChange = (content: string) => {
    // Remove "fake" empty lines (empty paragraphs with <br>) to prevent artificial spacing
    // const newValue = content.replace(/<p[^>]*><br><\/p>/g, '');
    setFieldValue(id, content);
  };

  const [isFocused, setIsFocused] = useState(false);

  const handleBlur = () => {
    setFieldTouched(id, true);
    setIsFocused(false);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const quillClassName = useMemo(() => {
    const classes = [];
    if (isTouched && Boolean(error)) classes.push('error');
    if (isFocused) classes.push('focused');
    return classes.join(' ');
  }, [isTouched, error, isFocused]);

  return (
    <FormControl className={className} error={isTouched && Boolean(error)} fullWidth>
      {unifiedLabel && (
        <InputLabel htmlFor={`${id}`} shrink style={{ top: '-8px' }}>
          {unifiedLabel}
        </InputLabel>
      )}
      <div style={{ marginTop: unifiedLabel ? 8 : 0 }}>
        <div className={quillClassName}>
          <ReactQuill
            formats={formats || defaultFormats}
            modules={modules || defaultModules}
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={handleFocus}
            placeholder={placeholder}
            readOnly={readonly || disabled}
            style={{ height: 'auto', minHeight: `${height}px` }}
            theme='snow'
            value={value}
          />
        </div>
      </div>
      {(isTouched || readonly) && (
        <FormHelperText data-cy={`${id}_input_error`} error={isTouched && Boolean(error)} style={{ position: 'relative', top: '45px' }}>
          {isTouched && error}
          {readonly && readonlyInfo}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default FormRichTextInput;
